/**
 * Reply Intent — Phase 7g card.
 *
 * Shows how members RESPONDED to outreach SMS. Three layers:
 *   1. Quality bar — replies_quality / replies_opt_out / replies_unclassified
 *      per federation. The 1.07x enrollment calibration is meaningful only
 *      to the extent that replies skew toward "quality" buckets.
 *   2. Top intent stack per federation (the 15-bucket LLM classification)
 *   3. Click any intent label to load 10 redacted sample bodies inline.
 *
 * Internal CBO only — backend 403s for everyone else.
 */

import React, { useMemo, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useReplyIntent, useReplyIntentSamples } from '@/hooks/ml';

const INTENT_COLOR: Record<string, string> = {
  AFFIRMATIVE_INTEREST: 'bg-emerald-500',
  ENROLLMENT_REQUEST: 'bg-emerald-600',
  ELIGIBILITY_QUESTION: 'bg-sky-500',
  PROGRAM_QUESTION: 'bg-sky-400',
  APPOINTMENT_CONFIRM: 'bg-blue-500',
  APPOINTMENT_RESCHEDULE: 'bg-amber-400',
  APPOINTMENT_DECLINE: 'bg-amber-500',
  CALLBACK_REQUEST: 'bg-indigo-500',
  OPT_OUT: 'bg-rose-600',
  WRONG_NUMBER: 'bg-orange-400',
  LANGUAGE_REQUEST: 'bg-fuchsia-500',
  COMPLAINT_FRUSTRATION: 'bg-rose-500',
  GRATITUDE: 'bg-teal-400',
  TECHNICAL_ISSUE: 'bg-yellow-500',
  OTHER: 'bg-gray-400',
  _unclassified_: 'bg-gray-300',
};

// Backend may return numeric fields as strings (Decimal → JSON); coerce.
const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmt = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : Math.round(n).toLocaleString();
};


export const ReplyIntentCard: React.FC = () => {
  const [selectedFed, setSelectedFed] = useState<string | null>(null);
  const ri = useReplyIntent(selectedFed);
  const [expandedIntent, setExpandedIntent] = useState<string | null>(null);
  const samples = useReplyIntentSamples(expandedIntent);

  const federations = useMemo(() => {
    const codes = new Set<string>();
    (ri.data?.quality ?? []).forEach((q) => codes.add(q.federation_code));
    return Array.from(codes);
  }, [ri.data]);

  const activeFed = selectedFed ?? federations[0] ?? null;

  // Intent buckets EXCLUDING OPT_OUT — the stack bar and percentages all
  // weight against the engaged-reply universe, not the full reply count.
  // OPT_OUTs are reported separately in the quality summary.
  const intentTotals = useMemo(() => {
    const map = new Map<string, { count: number; members: number; opt_outs: number }>();
    for (const r of ri.data?.rollup ?? []) {
      if (activeFed && r.federation_code !== activeFed) continue;
      if (r.intent_label === 'OPT_OUT') continue;
      const cur = map.get(r.intent_label) ?? { count: 0, members: 0, opt_outs: 0 };
      cur.count += Number(r.reply_count) || 0;
      cur.members += Number(r.unique_members) || 0;
      cur.opt_outs += Number(r.opt_outs) || 0;
      map.set(r.intent_label, cur);
    }
    return Array.from(map.entries())
      .map(([intent_label, v]) => ({ intent_label, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [ri.data, activeFed]);

  // Denominator for percentages = replies EXCLUDING opt-outs.
  const totalReplies = intentTotals.reduce((a, x) => a + x.count, 0);

  if (ri.isLoading) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">Loading reply intent…</CardContent>
      </Card>
    );
  }
  if (ri.isError) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">
          Reply intent unavailable. (Internal CBO only; needs the classifier cron to have run.)
        </CardContent>
      </Card>
    );
  }
  if (!totalReplies) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">
          No classified replies yet. Run the legacy-trust-intent-classifier cron to populate
          legacy_trust.reply_intent_llm.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        {/* Federation selector */}
        {federations.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-gray-500">Federation:</span>
            {federations.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setSelectedFed(f)}
                className={`rounded-full border px-2.5 py-0.5 font-medium ${
                  f === activeFed
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        )}

        {/* Intent stack bar — engaged replies only.
            (Quality-replies / opt-out rate summary intentionally removed —
            those headline percentages distract from the engagement narrative
            and are not part of the analysis exposed to clients.) */}
        <div>
          <div className="text-xs text-gray-500 mb-1">
            Reply intent distribution
          </div>
          <div className="flex h-6 w-full overflow-hidden rounded-md border border-gray-200">
            {intentTotals.map((b) => {
              const w = totalReplies > 0 ? (b.count / totalReplies) * 100 : 0;
              if (w < 0.3) return null;
              return (
                <div
                  key={b.intent_label}
                  className={INTENT_COLOR[b.intent_label] ?? 'bg-gray-400'}
                  style={{ width: `${w}%` }}
                  title={`${b.intent_label}: ${fmt(b.count)} (${w.toFixed(1)}% of engaged replies)`}
                />
              );
            })}
          </div>
        </div>

        {/* Per-intent list with drill-down */}
        <div className="space-y-1">
          {intentTotals.map((b) => {
            const isExpanded = expandedIntent === b.intent_label;
            const pctVal = (b.count / totalReplies) * 100;
            return (
              <div key={b.intent_label} className="rounded border border-gray-200">
                <button
                  type="button"
                  onClick={() =>
                    setExpandedIntent(isExpanded ? null : b.intent_label)
                  }
                  className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-xs hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-3 w-3 text-gray-400" />
                    )}
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${
                        INTENT_COLOR[b.intent_label] ?? 'bg-gray-400'
                      }`}
                    />
                    <span className="font-medium text-gray-800">{b.intent_label}</span>
                  </div>
                  <div className="flex items-center gap-3 tabular-nums text-gray-600">
                    <span>{fmt(b.count)}</span>
                    <span className="w-12 text-right">{pctVal.toFixed(1)}%</span>
                    <span className="hidden text-gray-400 md:inline">
                      {fmt(b.members)} members
                    </span>
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2 text-xs">
                    {samples.isLoading ? (
                      <div className="text-gray-500">Loading samples…</div>
                    ) : samples.data?.samples.length ? (
                      <div className="space-y-1.5">
                        <div className="text-[10px] text-gray-500">
                          Sample replies (redacted; classifier confidence-sorted)
                        </div>
                        {samples.data.samples.map((s) => (
                          <div
                            key={s.body_norm_hash}
                            className="rounded bg-white border border-gray-200 px-2 py-1 flex items-start justify-between gap-2"
                          >
                            <span className="text-gray-800 break-words">"{s.sample_body}"</span>
                            <span className="flex items-center gap-1 text-[10px] text-gray-500 flex-shrink-0">
                              {s.detected_language}
                              {s.confidence != null && ` · ${(s.confidence * 100).toFixed(0)}%`}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <AlertCircle className="h-3 w-3" />
                        No clean samples for this intent (PII filter applied).
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReplyIntentCard;
